//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { TableHeaderCellText } from '@components/atoms/text/Text';
import { TableHeaderCell } from '@components/organisms/table/TableHeaderCell';
import { isDefined } from '@utils/filterPredicates';
import { ITrackableComponent, withTracking } from '@utils/withTracking';

interface Props
  extends React.HTMLAttributes<HTMLTableElement>,
    ITrackableComponent {
  body: React.ReactNode;
  caption?: React.ReactNode;
  captionId?: string;
  footer?: React.ReactNode;
  headerCells: React.ReactNode[];
}

/**
 * This component exists for the purpose of standardizing different tables.
 *
 * @param captionId Optional id to use for `aria-describedby` or `aria-labelledby` when grouping table elements.
 * e.g.:
 * <div role='group' aria-labelledby={captionId}>
 *    <Table captionId={captionId} ... />
 *    <AdditionalComponent />
 * </div>
 * @param caption Component that displays the table's title/description and is wrapped in the table's `<caption>` element and is displayed
 * above the table. Consists of `TableCaptionTitle` and any additional buttons, filter/sort/search bars, etc.
 * @param headerCells An array of the headers that comprise the table's `<thead>` element,
 * e.g.: ['header1', 'header2', <CustomElement />, 'header4']. The Table component will render each header
 * as a TableHeaderCell, which is a `<th>` element, and the Table component will wrap the `<th>` elements in
 * a `<tr>` element in the `<thead>`.
 * @param body An array of rows that comprise the table's `<tbody>` element and consists of `TableRow` components.
 * @param footer Optional component that is wrapped in the table's `<tfoot>` element and is displayed at the bottom of the table.
 * Defines a set of rows summarizing the columns of the table. May contain `TableRow`, `TableDataCell`, and `TableHeaderCell` (with scope='row') components.
 */
export const Table = ({
  body,
  caption,
  captionId,
  className,
  footer,
  headerCells,
  trackingId,
  ...tableProps
}: Props) => {
  return (
    <StyledTable className={className} trackingId={trackingId} {...tableProps}>
      {isDefined(caption) && (
        <StyledCaption {...(!!captionId && { id: captionId })}>
          {caption}
        </StyledCaption>
      )}
      <StyledTHead>
        <tr>
          {headerCells.map((cell, idx) => {
            return (
              <TableHeaderCell
                key={typeof cell === 'string' ? cell : idx}
                scope='col'
              >
                {typeof cell === 'string' ? (
                  <TableHeaderCellText
                    justifyContent={idx > 0 ? 'flex-end' : 'flex-start'}
                  >
                    {cell}
                  </TableHeaderCellText>
                ) : (
                  cell
                )}
              </TableHeaderCell>
            );
          })}
        </tr>
      </StyledTHead>
      <tbody>{body}</tbody>
      {!!footer && <tfoot>{footer}</tfoot>}
    </StyledTable>
  );
};

const StyledTable = withTracking(styled.table`
  width: 100%;
  border-collapse: collapse;
`);

const StyledCaption = styled.caption(
  ({ theme }) => `
      margin-bottom: ${theme.spacings[3]};
    `,
);

const StyledTHead = styled.thead(
  ({ theme }) => `
    border: ${theme.borderWidths[1]} solid ${theme.colors.gray1};
    background-color: ${theme.colors.gray0};
    `,
);
